// @ts-check
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { Check, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import DiffHunk from "./DiffHunk"

/** @param {string | undefined} text */
function oneLine(text) {
  return (text ?? "").replace(/\s+/g, " ").trim()
}

/**
 * The second line of a row: what changes, or why the row has no card in the text.
 * @param {{ isFullRewrite?: boolean, old_string?: string, new_string: string, status: string }} diff
 * @param {boolean} isPlaced
 */
function rowDetail(diff, isPlaced) {
  if (diff.status === "stale") return "The text has changed, so this no longer applies."
  if (diff.isFullRewrite) return "Rewrites the whole document. Select to compare."
  if (!isPlaced) return "Not shown in the text. Select to compare."
  const removed = oneLine(diff.old_string)
  const added = oneLine(diff.new_string)
  if (!added) return `Delete: ${removed}`
  return `${removed} → ${added}`
}

/**
 * List of all suggestions, opened from the review bar. A row with a card in
 * the document jumps to it; any other row opens its comparison here, because
 * this list is the only place to act on it.
 * @param {{
 *   diffs: Array<{ id: string, isFullRewrite?: boolean, old_string?: string, new_string: string, rationale: string, status: "pending" | "stale" }>,
 *   placedIds: Set<string>,
 *   activeDiffId: string | null,
 *   onSelect: (diffId: string) => void,
 *   onAccept: (diffId: string) => void,
 *   onReject: (diffId: string) => void,
 *   onClose: () => void,
 * }} props
 */
export default function DiffOverlay({ diffs, placedIds, activeDiffId, onSelect, onAccept, onReject, onClose }) {
  const reduceMotion = useReducedMotion()
  const pendingCount = diffs.filter(diff => diff.status === "pending").length

  return (
    <motion.section
      aria-label="Suggestions"
      initial={reduceMotion ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      onKeyDown={(e) => { if (e.key === "Escape") onClose() }}
      className="flex flex-col max-h-[45%] min-h-0 shrink-0 border-t border-border bg-background"
    >
      <div className="flex items-center justify-between h-9 pl-4 pr-2 shrink-0 border-b border-border">
        <h2 className="text-xs font-medium text-foreground">
          Suggestions <span className="text-muted-foreground">· {pendingCount} to review</span>
        </h2>
        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Close list" onClick={onClose}>
          <X size={14} />
        </Button>
      </div>

      <ul className="flex-1 min-h-0 overflow-y-auto divide-y divide-border/60">
        <AnimatePresence initial={false}>
          {diffs.map((diff) => {
            const isPlaced = placedIds.has(diff.id)
            const isActive = activeDiffId === diff.id
            const isStale = diff.status === "stale"
            return (
              <motion.li
                key={diff.id}
                layout={reduceMotion ? false : "position"}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 16 }}
                transition={{ duration: 0.16 }}
                className={cn(isActive && "bg-muted/60")}
              >
                <div className="flex items-start gap-1.5 pl-4 pr-2 py-2">
                  <button
                    type="button"
                    className="flex-1 min-w-0 rounded-md bg-transparent px-1 -mx-1 text-left transition-colors hover:bg-muted/50"
                    aria-expanded={isPlaced ? undefined : isActive}
                    onClick={() => onSelect(diff.id)}
                  >
                    <span className="line-clamp-2 text-[13px] leading-5 text-foreground">
                      {diff.rationale || "Suggested edit"}
                    </span>
                    <span className={cn("mt-0.5 block truncate text-xs", isStale ? "text-yellow-700 dark:text-yellow-400" : "text-muted-foreground")}>
                      {rowDetail(diff, isPlaced)}
                    </span>
                  </button>
                  <div data-diff-action className="flex items-center gap-0.5 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      aria-label={isStale ? "Dismiss suggestion" : "Reject suggestion"}
                      title={isStale ? "Dismiss" : "Reject"}
                      onClick={() => onReject(diff.id)}
                    >
                      <X size={14} />
                    </Button>
                    {!isStale && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-green-700 hover:bg-green-500/10 hover:text-green-700 dark:text-green-400 dark:hover:text-green-400"
                        aria-label="Accept suggestion"
                        title="Accept"
                        onClick={() => onAccept(diff.id)}
                      >
                        <Check size={14} />
                      </Button>
                    )}
                  </div>
                </div>
                {!isPlaced && isActive && !isStale && (
                  <div className="px-4 pb-1">
                    <DiffHunk diff={diff} onAccept={onAccept} onReject={onReject} />
                  </div>
                )}
              </motion.li>
            )
          })}
        </AnimatePresence>
      </ul>
    </motion.section>
  )
}
